import { computed } from "vue";
import { useMainStore } from "@/composables/store";
import {
  convertDisplayAmount,
  formatDisplayMoney,
  formatDisplayRate,
  getCurrencyFlag,
  getCurrencySymbol,
  normalizeCurrencyCode,
  normalizeDisplayLocale,
  resolveDisplayMoneyDecimals,
} from "@/utils/displayCurrency";

export function useDisplayCurrency() {
  const store = useMainStore();
  const { locale } = useI18n();
  const currentCurrency = computed(() => store.currentCurrency || {});
  const currencyList = computed(() => {
    return Array.isArray(store.currencyList) ? store.currencyList : [];
  });
  const displayLocale = computed(() => {
    return normalizeDisplayLocale(locale.value);
  });
  const baseCurrencyCode = computed(() => {
    const fallbackCurrencyCode =
      normalizeCurrencyCode(store.getMallSiteDefaultCurrency?.()) || "TWD";
    return (
      normalizeCurrencyCode(
        currentCurrency.value?.fromCurrencyCode ||
          currentCurrency.value?.baseCurrencyCode ||
          currencyList.value?.[0]?.fromCurrencyCode ||
          store.getMallSiteDefaultCurrency?.()
      ) || fallbackCurrencyCode
    );
  });
  const displayCurrencyCode = computed(() => {
    return (
      normalizeCurrencyCode(currentCurrency.value?.toCurrencyCode) ||
      baseCurrencyCode.value
    );
  });
  const displayCurrencySymbol = computed(() => {
    return getCurrencySymbol(displayCurrencyCode.value);
  });
  const displayCurrencyFlag = computed(() => {
    return getCurrencyFlag(displayCurrencyCode.value);
  });

  const convertAmount = (
    amount,
    fromCurrencyCode = baseCurrencyCode.value,
    options = {}
  ) => {
    return convertDisplayAmount(amount, {
      fromCurrencyCode,
      toCurrencyCode:
        normalizeCurrencyCode(options.toCurrencyCode) || displayCurrencyCode.value,
      currentCurrency: currentCurrency.value,
      currencyList: currencyList.value,
    });
  };

  const formatMoney = (
    amount,
    fromCurrencyCode = baseCurrencyCode.value,
    options = {}
  ) => {
    const normalizedFromCurrencyCode =
      normalizeCurrencyCode(fromCurrencyCode) || baseCurrencyCode.value;
    const resolvedCurrencyCode =
      normalizeCurrencyCode(options.toCurrencyCode) || displayCurrencyCode.value;
    const convertedAmount = convertAmount(amount, fromCurrencyCode, {
      toCurrencyCode: resolvedCurrencyCode,
    });
    const resolvedDecimals = resolveDisplayMoneyDecimals({
      currencyCode: resolvedCurrencyCode,
      fromCurrencyCode: normalizedFromCurrencyCode,
      baseCurrencyCode: baseCurrencyCode.value,
      decimals: options.decimals,
    });

    return formatDisplayMoney(convertedAmount, {
      currencyCode: resolvedCurrencyCode,
      locale: options.locale || displayLocale.value,
      decimals: resolvedDecimals,
      withCode: options.withCode,
      trimTrailingZeros: options.trimTrailingZeros,
      spaceBetweenSymbolAndAmount: options.spaceBetweenSymbolAndAmount,
    });
  };

  const formatOriginalMoney = (amount, currencyCode, options = {}) => {
    return formatDisplayMoney(amount, {
      currencyCode,
      locale: options.locale || displayLocale.value,
      decimals: options.decimals,
      withCode: options.withCode,
      trimTrailingZeros: options.trimTrailingZeros,
      spaceBetweenSymbolAndAmount: options.spaceBetweenSymbolAndAmount,
    });
  };

  const formatRate = (rate, options = {}) => {
    return formatDisplayRate(rate, {
      locale: options.locale || displayLocale.value,
      minimumFractionDigits: options.minimumFractionDigits,
      maximumFractionDigits: options.maximumFractionDigits,
    });
  };

  return {
    baseCurrencyCode,
    convertAmount,
    currencyList,
    currentCurrency,
    displayCurrencyFlag,
    displayCurrencyCode,
    displayCurrencySymbol,
    displayLocale,
    formatMoney,
    formatOriginalMoney,
    formatRate,
  };
}
